/**
 * Reuniones IA — graba/sube reuniones, se transcriben (whisper) y resumen
 * (Ollama) 100% self-hosted. Privadas por usuario: cada quien ve las suyas y
 * las que le comparten. El audio se transmite a traves del backend (nunca se
 * expone el MinIO dedicado). Gated por permisos meetings:read/manage/share.
 */
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  MicrophoneIcon,
  PlusIcon,
  TrashIcon,
  ChevronLeftIcon,
  ArrowUpTrayIcon,
  StopIcon,
  ShareIcon,
  ArrowPathIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner } from '@/components/common';
import { meetingsApi, type Meeting } from '@/services/api';
import { useOrganizationStore } from '@/stores/organization';
import { PERMISSIONS } from '@/types';

const cardClass = 'bg-white rounded-lg border border-gray-200 shadow-sm';
const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';

// ─── Exportar acta (Markdown por descarga · PDF vía impresión del navegador) ───
function actaFilename(meeting: Meeting, ext: string): string {
  const base = (meeting.title || 'acta')
    .toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'acta';
  return `acta-${base}.${ext}`;
}

function actaDate(meeting: Meeting): string {
  return meeting.created_at
    ? new Date(meeting.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';
}

function buildActaMarkdown(meeting: Meeting): string {
  const s = meeting.summary;
  const out: string[] = [`# Acta — ${meeting.title || 'Reunión'}`];
  const date = actaDate(meeting);
  if (date) out.push('', `*${date}*`);
  if (s?.text) out.push('', '## Resumen', '', s.text);
  if (s?.keyPoints?.length) {
    out.push('', '## Puntos clave');
    for (const kp of s.keyPoints) {
      if (kp.topic) out.push('', `### ${kp.topic}`);
      for (const p of kp.points) out.push(`- ${p}`);
    }
  }
  const section = (title: string, items?: string[]) => {
    if (items && items.length) { out.push('', `## ${title}`); for (const it of items) out.push(`- ${it}`); }
  };
  section('Acuerdos', s?.agreements);
  section('Decisiones', s?.decisions);
  section('Tareas', s?.tasks);
  section('Riesgos', s?.risks);
  return out.join('\n') + '\n';
}

function downloadMarkdown(meeting: Meeting): void {
  const blob = new Blob([buildActaMarkdown(meeting)], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = actaFilename(meeting, 'md');
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function buildActaHtml(meeting: Meeting): string {
  const s = meeting.summary;
  const date = actaDate(meeting);
  const parts: string[] = [`<h1>Acta — ${escHtml(meeting.title || 'Reunión')}</h1>`];
  if (date) parts.push(`<p class="date">${escHtml(date)}</p>`);
  if (s?.text) parts.push(`<h2>Resumen</h2><p>${escHtml(s.text)}</p>`);
  if (s?.keyPoints?.length) {
    parts.push('<h2>Puntos clave</h2>');
    for (const kp of s.keyPoints) {
      if (kp.topic) parts.push(`<h3>${escHtml(kp.topic)}</h3>`);
      if (kp.points.length) parts.push('<ul>' + kp.points.map((p) => `<li>${escHtml(p)}</li>`).join('') + '</ul>');
    }
  }
  const section = (title: string, items?: string[]) => {
    if (items && items.length) parts.push(`<h2>${title}</h2><ul>` + items.map((it) => `<li>${escHtml(it)}</li>`).join('') + '</ul>');
  };
  section('Acuerdos', s?.agreements);
  section('Decisiones', s?.decisions);
  section('Tareas', s?.tasks);
  section('Riesgos', s?.risks);
  return `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${escHtml(meeting.title || 'Acta')}</title>
<style>
  body { font-family: -apple-system, "Segoe UI", Roboto, sans-serif; color: #1f2937; max-width: 720px; margin: 32px auto; padding: 0 24px; line-height: 1.5; }
  h1 { font-size: 24px; margin-bottom: 4px; }
  .date { color: #6b7280; font-style: italic; margin-top: 0; }
  h2 { font-size: 16px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; margin-top: 24px; }
  h3 { font-size: 14px; margin: 12px 0 4px; }
  ul { margin: 4px 0 0; padding-left: 20px; }
  li { margin: 2px 0; }
  @media print { body { margin: 0 auto; } }
</style></head><body>${parts.join('')}</body></html>`;
}

// Abre una ventana con el acta maquetada y lanza el diálogo de impresión del
// navegador (el usuario elige «Guardar como PDF»). Sin librerías extra.
function printActaPdf(meeting: Meeting): void {
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(buildActaHtml(meeting));
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
}
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

type Scope = 'all' | 'mine' | 'shared';

const STATUS_LABEL: Record<Meeting['status'], string> = {
  recording: 'Grabando',
  uploaded: 'Subida',
  transcribing: 'Procesando…',
  ready: 'Lista',
  failed: 'Error',
};
const STATUS_COLOR: Record<Meeting['status'], string> = {
  recording: 'bg-gray-100 text-gray-600',
  uploaded: 'bg-blue-100 text-blue-700',
  transcribing: 'bg-amber-100 text-amber-700',
  ready: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

function StatusBadge({ status }: { status: Meeting['status'] }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[status]}`}>
      {STATUS_LABEL[status]}
    </span>
  );
}

// Colores por interlocutor (se reciclan si hay más de 6).
const SPEAKER_COLORS = [
  'text-vida-700', 'text-blue-700', 'text-amber-700',
  'text-purple-700', 'text-pink-700', 'text-teal-700',
];

/** Agrupa segmentos diarizados por turnos consecutivos del mismo hablante y
 *  mapea SPEAKER_XX → "Interlocutor N" con un color estable por hablante. */
function groupBySpeaker(
  segments: NonNullable<Meeting['transcript']>['segments']
): { label: string; text: string; color: string }[] {
  const list = segments || [];
  const infoBySpeaker = new Map<string, { label: string; color: string }>();
  const infoFor = (spk?: string) => {
    const key = spk || 'SPEAKER_?';
    if (!infoBySpeaker.has(key)) {
      const idx = infoBySpeaker.size;
      infoBySpeaker.set(key, {
        label: `Interlocutor ${idx + 1}`,
        color: SPEAKER_COLORS[idx % SPEAKER_COLORS.length],
      });
    }
    return infoBySpeaker.get(key)!;
  };
  const out: { label: string; text: string; color: string }[] = [];
  for (const s of list) {
    const info = infoFor(s.speaker);
    const last = out[out.length - 1];
    if (last && last.label === info.label) last.text += ' ' + s.text;
    else out.push({ label: info.label, text: s.text, color: info.color });
  }
  return out;
}

export default function Meetings() {
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const canManage = useOrganizationStore((s) => s.hasOrgPermission(PERMISSIONS.MEETINGS_MANAGE));
  const canShare = useOrganizationStore((s) => s.hasOrgPermission(PERMISSIONS.MEETINGS_SHARE));

  const [items, setItems] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState<Scope>('all');
  const [selected, setSelected] = useState<Meeting | null>(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await meetingsApi.list({ scope, limit: 100 });
      setItems(res.data.meetings || []);
    } catch {
      toast.error('No se pudieron cargar las reuniones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId, scope]);

  // Auto-refresco de la lista mientras haya reuniones en curso (subiendo o
  // transcribiendo), para que pasen a «Lista» solas sin recargar la página.
  const hasPending = items.some(
    (m) => m.status === 'transcribing' || m.status === 'uploaded' || m.status === 'recording'
  );
  useEffect(() => {
    if (!hasPending) return;
    const t = setInterval(() => {
      void load();
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPending, scope, selectedOrgId]);

  if (selected) {
    return (
      <MeetingDetail
        meeting={selected}
        canShare={canShare}
        canManage={canManage}
        onBack={() => {
          setSelected(null);
          void load();
        }}
      />
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <MicrophoneIcon className="w-7 h-7 text-vida-600" /> Reuniones
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Graba o sube una reunión; se transcribe y resume con IA privada. Solo tú
            la ves salvo que la compartas.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700"
          >
            <PlusIcon className="w-5 h-5" /> Nueva reunión
          </button>
        )}
      </div>

      {/* Scope tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
        {(['all', 'mine', 'shared'] as Scope[]).map((s) => (
          <button
            key={s}
            onClick={() => setScope(s)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              scope === s ? 'bg-white text-vida-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {s === 'all' ? 'Todas' : s === 'mine' ? 'Mías' : 'Compartidas'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : items.length === 0 ? (
        <div className={`${cardClass} p-10 text-center text-gray-500`}>
          No hay reuniones todavía.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((m) => (
            <button
              key={m._id}
              onClick={() => setSelected(m)}
              className={`${cardClass} w-full text-left p-4 hover:border-vida-300 transition-colors flex items-center justify-between gap-4`}
            >
              <div className="min-w-0">
                <p className="font-medium text-gray-900 truncate">{m.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {new Date(m.created_at).toLocaleString('es-ES')}
                  {m.sharedWith?.length ? ` · compartida con ${m.sharedWith.length}` : ''}
                </p>
              </div>
              <StatusBadge status={m.status} />
            </button>
          ))}
        </div>
      )}

      {creating && (
        <NewMeetingModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void load();
          }}
        />
      )}
    </div>
  );
}

// ---------- Nueva reunión (grabar o subir) ----------

function NewMeetingModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState('');
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [busy, setBusy] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        setBlob(new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' }));
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error('No se pudo acceder al micrófono');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setBlob(f);
  };

  const submit = async () => {
    if (!title.trim()) {
      toast.error('Pon un título');
      return;
    }
    if (!blob) {
      toast.error('Graba o sube un audio');
      return;
    }
    setBusy(true);
    try {
      const created = await meetingsApi.create({ title: title.trim() });
      const id = created.data.meeting._id as string;
      await meetingsApi.uploadAudio(id, blob);
      await meetingsApi.complete(id);
      toast.success('Reunión subida; se está transcribiendo');
      onCreated();
    } catch {
      toast.error('No se pudo crear la reunión');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className={`${cardClass} w-full max-w-md p-6`}>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Nueva reunión</h2>

        <label className={labelClass}>Título</label>
        <input
          className={inputClass}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ej: Junta mensual junio"
        />

        <div className="mt-5 space-y-3">
          {!recording ? (
            <button
              onClick={startRecording}
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 border border-vida-300 text-vida-700 rounded-lg text-sm font-medium hover:bg-vida-50"
            >
              <MicrophoneIcon className="w-5 h-5" /> Grabar
            </button>
          ) : (
            <button
              onClick={stopRecording}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 animate-pulse"
            >
              <StopIcon className="w-5 h-5" /> Detener grabación
            </button>
          )}

          <label className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 cursor-pointer">
            <ArrowUpTrayIcon className="w-5 h-5" /> Subir archivo de audio
            <input type="file" accept="audio/*" className="hidden" onChange={onFile} />
          </label>

          {blob && (
            <p className="text-xs text-green-700 bg-green-50 rounded-md px-3 py-2">
              Audio listo ({Math.round(blob.size / 1024)} KB{blob.type ? ` · ${blob.type}` : ''})
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={busy || recording}
            className="px-4 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700 disabled:opacity-50"
          >
            {busy ? 'Subiendo…' : 'Crear'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Detalle ----------

function MeetingDetail({
  meeting: initial,
  canShare,
  canManage,
  onBack,
}: {
  meeting: Meeting;
  canShare: boolean;
  canManage: boolean;
  onBack: () => void;
}) {
  const [meeting, setMeeting] = useState<Meeting>(initial);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);

  const refresh = async () => {
    try {
      const res = await meetingsApi.get(meeting._id);
      setMeeting(res.data.meeting);
    } catch {
      /* noop */
    }
  };

  // Auto-poll mientras se transcribe.
  useEffect(() => {
    if (meeting.status !== 'transcribing') return;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting.status]);

  // Cargar audio como blob (el <audio> no puede llevar el header de auth).
  useEffect(() => {
    let revoke: string | null = null;
    (async () => {
      if (!meeting.audio) return;
      try {
        const res = await meetingsApi.audioBlob(meeting._id);
        const url = URL.createObjectURL(res.data as Blob);
        revoke = url;
        setAudioUrl(url);
      } catch {
        /* sin audio reproducible */
      }
    })();
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting._id]);

  const remove = async () => {
    if (!confirm('¿Borrar esta reunión y su audio?')) return;
    try {
      await meetingsApi.remove(meeting._id);
      toast.success('Reunión borrada');
      onBack();
    } catch {
      toast.error('No se pudo borrar');
    }
  };

  const s = meeting.summary;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ChevronLeftIcon className="w-4 h-4" /> Volver
      </button>

      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{meeting.title}</h1>
          <p className="text-xs text-gray-500 mt-1">{new Date(meeting.created_at).toLocaleString('es-ES')}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={meeting.status} />
          {meeting.status === 'transcribing' && (
            <button onClick={refresh} className="p-1.5 text-gray-400 hover:text-gray-600" title="Refrescar">
              <ArrowPathIcon className="w-5 h-5" />
            </button>
          )}
          {canShare && (
            <button onClick={() => setSharing(true)} className="p-1.5 text-gray-400 hover:text-vida-600" title="Compartir">
              <ShareIcon className="w-5 h-5" />
            </button>
          )}
          {canManage && (
            <button onClick={remove} className="p-1.5 text-gray-400 hover:text-red-600" title="Borrar">
              <TrashIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {audioUrl && (
        <div className={`${cardClass} p-4 mb-4`}>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio
            controls
            src={audioUrl}
            className="w-full"
            preload="metadata"
            onLoadedMetadata={(e) => {
              // El audio grabado con MediaRecorder (webm/opus) no lleva la duración
              // en la cabecera → el reproductor muestra 0:00 y no deja seek/play.
              // Truco estándar: saltar al final para forzar al navegador a calcular
              // la duración real, y volver al inicio.
              const a = e.currentTarget;
              if (a.duration === Infinity || Number.isNaN(a.duration)) {
                const onTimeUpdate = () => {
                  a.removeEventListener('timeupdate', onTimeUpdate);
                  a.currentTime = 0;
                };
                a.addEventListener('timeupdate', onTimeUpdate);
                a.currentTime = 1e101;
              }
            }}
          />
        </div>
      )}

      {meeting.status === 'failed' && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-4 text-sm">
          Error al procesar: {meeting.error || 'desconocido'}
        </div>
      )}

      {meeting.status === 'transcribing' && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 mb-4 text-sm flex items-center gap-2">
          <LoadingSpinner /> Transcribiendo y resumiendo con IA… esto puede tardar según la duración.
        </div>
      )}

      {s && (s.text || s.keyPoints?.length || s.agreements?.length || s.tasks?.length || s.decisions?.length || s.risks?.length) ? (
        <div className={`${cardClass} p-5 mb-4`}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <DocumentTextIcon className="w-5 h-5 text-vida-600" /> Resumen IA
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => downloadMarkdown(meeting)}
                className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                title="Descargar el acta en Markdown (.md)"
              >
                Markdown
              </button>
              <button
                onClick={() => printActaPdf(meeting)}
                className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                title="Imprimir / Guardar como PDF"
              >
                PDF
              </button>
            </div>
          </div>
          {s.text && <p className="text-sm text-gray-700 whitespace-pre-wrap mb-4">{s.text}</p>}

          {/* Puntos clave agrupados por tema (con quién lo dijo cuando se sabe) */}
          {s.keyPoints && s.keyPoints.length > 0 && (
            <div className="mb-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Puntos clave</h3>
              <div className="space-y-3">
                {s.keyPoints.map((kp, i) => (
                  <div key={i}>
                    {kp.topic && <p className="text-sm font-medium text-gray-800">{kp.topic}</p>}
                    <ul className="mt-1 space-y-0.5">
                      {kp.points.map((p, j) => (
                        <li key={j} className="text-sm text-gray-600 flex gap-2">
                          <span className="text-vida-500 mt-0.5">•</span><span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <SummaryList title="Acuerdos" items={s.agreements} />
            <SummaryList title="Tareas" items={s.tasks} />
            <SummaryList title="Decisiones" items={s.decisions} />
            <SummaryList title="Riesgos" items={s.risks} />
          </div>
        </div>
      ) : null}

      {meeting.transcript?.text && (
        <details className={`${cardClass} p-5`}>
          <summary className="text-sm font-semibold text-gray-900 cursor-pointer">
            Transcripción completa{meeting.transcript.diarized ? ' (por interlocutor)' : ''}
          </summary>
          {meeting.transcript.diarized && meeting.transcript.segments?.length ? (
            <div className="mt-3 space-y-3">
              {groupBySpeaker(meeting.transcript.segments).map((turn, i) => (
                <div key={i} className="text-sm">
                  <span className={`font-semibold ${turn.color}`}>{turn.label}:</span>{' '}
                  <span className="text-gray-700">{turn.text}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-600 whitespace-pre-wrap mt-3">{meeting.transcript.text}</p>
          )}
        </details>
      )}

      {sharing && (
        <ShareModal
          meeting={meeting}
          onClose={() => setSharing(false)}
          onChanged={(m) => setMeeting(m)}
        />
      )}
    </div>
  );
}

function SummaryList({ title, items }: { title: string; items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{title}</p>
      <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    </div>
  );
}

// ---------- Compartir ----------

interface OrgUser {
  id: string;
  name: string;
  email: string;
}

function ShareModal({
  meeting,
  onClose,
  onChanged,
}: {
  meeting: Meeting;
  onClose: () => void;
  onChanged: (m: Meeting) => void;
}) {
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [shared, setShared] = useState<string[]>(meeting.sharedWith || []);

  useEffect(() => {
    (async () => {
      try {
        const res = await meetingsApi.orgUsers();
        setUsers(res.data.users || []);
      } catch {
        toast.error('No se pudieron cargar los usuarios');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = async (userId: string) => {
    try {
      if (shared.includes(userId)) {
        const res = await meetingsApi.unshare(meeting._id, userId);
        setShared(res.data.meeting.sharedWith || []);
        onChanged(res.data.meeting);
      } else {
        const res = await meetingsApi.share(meeting._id, [userId]);
        setShared(res.data.meeting.sharedWith || []);
        onChanged(res.data.meeting);
      }
    } catch {
      toast.error('No se pudo actualizar el compartido');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className={`${cardClass} w-full max-w-md p-6`}>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Compartir reunión</h2>
        <p className="text-xs text-gray-500 mb-4">
          Los usuarios seleccionados podrán ver esta reunión (solo lectura).
        </p>
        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="max-h-72 overflow-y-auto space-y-1">
            {users
              .filter((u) => u.id !== meeting.ownerUserId)
              .map((u) => (
                <label
                  key={u.id}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={shared.includes(u.id)}
                    onChange={() => toggle(u.id)}
                    className="rounded border-gray-300 text-vida-600 focus:ring-vida-500"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm text-gray-900 truncate">{u.name || u.email}</span>
                    {u.name && <span className="block text-xs text-gray-400 truncate">{u.email}</span>}
                  </span>
                </label>
              ))}
          </div>
        )}
        <div className="flex justify-end mt-5">
          <button onClick={onClose} className="px-4 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700">
            Hecho
          </button>
        </div>
      </div>
    </div>
  );
}
